// Magic-link sign-in surface for non-Shopify users.
//
// Layout: two-column on desktop. Left side: brand, value-prop lede,
// three benefit columns, HIPAA trust pill. Right side: the actual
// sign-in card (email field + "Email me a link" button) or the
// "Check your email" confirmation. On mobile collapses to one column.
//
// Flow: user enters email -> POST /api/auth/magic-link-request -> API
// emails a one-time link -> user clicks link -> SPA opens with
// ?token=... -> SessionProvider consumes -> user lands on Home.

import { Banner, TextField } from '@shopify/polaris';
import { useState } from 'react';
import { SeniorButton } from '../components/senior/SeniorButton';
import { env } from '../env';

export function Login(): JSX.Element {
  return (
    <div className="landing">
      <div className="landing__container">
        <ValueProp />
        <SignInPanel />
      </div>
    </div>
  );
}

function ValueProp(): JSX.Element {
  return (
    <div>
      {/* Round-3 polish: kitchen-table hero illustration sets the
          calm-trusted-pharmacist tone before the user reads a single
          word. Hidden on small mobile so the form stays above the
          fold; renders on tablet+ via CSS aspect-ratio. */}
      <img
        src="/illustrations/login-hero.png"
        alt=""
        aria-hidden="true"
        className="landing__hero"
        loading="eager"
        decoding="async"
        style={{
          width: '100%',
          maxWidth: 560,
          height: 'auto',
          borderRadius: 16,
          marginBottom: 24,
          display: 'block',
        }}
      />
      <div className="landing__brand" aria-label="MedReview">
        <svg viewBox="0 0 32 32" aria-hidden="true">
          <rect x="6" y="3" width="20" height="26" rx="4" fill="#0f4c75" />
          <rect x="9" y="6" width="14" height="9" rx="1.5" fill="#ffffff" opacity="0.95" />
          <path d="M16 18v8M12 22h8" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
        MedReview
      </div>

      <h1 className="landing__lede">A trusted pharmacist in your pocket, built for life at 65+.</h1>
      <p className="landing__sub">
        Spot interactions, get plain-language answers about your medicines, and bring a clean list
        to every doctor visit. No password to remember.
      </p>

      <ul className="landing__benefits">
        <li className="landing__benefit">
          <span className="landing__benefit-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <path
                d="M9 12l2 2 4-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <p className="landing__benefit-title">Safety first</p>
          <p className="landing__benefit-body">
            We check every medicine against guidelines for older adults.
          </p>
        </li>
        <li className="landing__benefit">
          <span className="landing__benefit-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-5 4V6a1 1 0 0 1 1-1z"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <p className="landing__benefit-title">Ask Siggy</p>
          <p className="landing__benefit-body">
            The AI companion to Dr. Marlee Grounds, PharmD. Plain answers, any time.
          </p>
        </li>
        <li className="landing__benefit">
          <span className="landing__benefit-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect
                x="3"
                y="4"
                width="18"
                height="16"
                rx="2"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path d="M3 9h18" stroke="currentColor" strokeWidth="2" />
              <path d="M8 14h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </span>
          <p className="landing__benefit-title">One clean list</p>
          <p className="landing__benefit-body">Bring a printable summary to every appointment.</p>
        </li>
      </ul>

      <span className="landing__trust">
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z"
            fill="none"
            stroke="#0f4c75"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
        HIPAA-compliant. Your medication info is private and encrypted.
      </span>
    </div>
  );
}

function SignInPanel(): JSX.Element {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${env.apiBaseUrl}/api/auth/magic-link-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmed,
          returnUrl: window.location.origin,
        }),
      });
      if (!res.ok) {
        setError('Could not send the sign-in link. Please try again in a moment.');
        return;
      }
      setSent(true);
    } catch {
      setError('Could not reach MedReview. Please check your internet and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="landing__card">
        <h2 className="landing__card-title">Check your email</h2>
        <p className="landing__card-lead">
          We sent a sign-in link to <strong>{email}</strong>. Open that email on this device and tap
          the button inside. The link expires in 15 minutes.
        </p>
        <p className="landing__card-lead" style={{ fontSize: 15 }}>
          Don&apos;t see it? Check your spam folder. If still missing, you can request a new link.
        </p>
        <SeniorButton onClick={() => setSent(false)} fullWidth>
          Send another link
        </SeniorButton>
        <p className="landing__legal">
          By signing in you agree to OTCHealth&apos;s privacy policy. MedReview is for educational
          purposes and does not replace your doctor or pharmacist.
        </p>
      </div>
    );
  }

  return (
    <div className="landing__card">
      <h2 className="landing__card-title">Sign in with your email</h2>
      <p className="landing__card-lead">
        Enter your email and we will send you a one-time sign-in link. No password to remember.
      </p>
      {error ? <Banner tone="critical">{error}</Banner> : null}
      <TextField
        label="Your email address"
        type="email"
        autoComplete="email"
        value={email}
        onChange={setEmail}
        disabled={submitting}
        placeholder="you@example.com"
        inputMode="email"
      />
      <SeniorButton
        variant="primary"
        fullWidth
        onClick={() => {
          void onSubmit();
        }}
        disabled={submitting || email.trim().length === 0}
      >
        {submitting ? 'Sending...' : 'Email me a sign-in link'}
      </SeniorButton>
      <p className="landing__legal">
        By signing in you agree to OTCHealth&apos;s privacy policy. MedReview is for educational
        purposes and does not replace your doctor or pharmacist.
      </p>
    </div>
  );
}
